import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, CheckCircle, ArrowRight, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Newsletter Signup Component
 * Can be used as:
 * 1. Inline section (variant="section") - for permanent placement on page
 * 2. Soft prompt (variant="prompt") - floating prompt that appears after viewing resources
 */
const NewsletterSignup = ({ 
  variant = "section", 
  onClose, 
  source = "resources" 
}) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');
    
    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setStatus('success');
        setMessage(data.message);
        // Store in localStorage to prevent showing prompt again
        localStorage.setItem('newsletter_subscribed', 'true');
      } else {
        setStatus('error');
        setMessage(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Unable to connect. Please try again later.');
    }
  };

  // Section variant - permanent placement on page
  if (variant === "section") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-forest rounded-2xl p-8 md:p-10 text-center"
      >
        <div className="max-w-xl mx-auto">
          <div className="w-14 h-14 bg-terracotta/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="text-terracotta" size={28} />
          </div>
          
          <h3 className="font-heading text-2xl md:text-3xl text-cream mb-3">
            Stay Grounded
          </h3>
          
          <p className="text-cream/80 font-body mb-6">
            Get weekly wellness insights, gentle reminders, and new resources 
            delivered to your inbox. No spam, just support for your journey.
          </p>

          <AnimatePresence mode="wait">
            {status === 'success' ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center justify-center gap-3 text-emerald-400"
              >
                <CheckCircle size={24} />
                <span className="font-body font-medium">{message}</span>
              </motion.div>
            ) : (
              <motion.form
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onSubmit={handleSubmit}
                className="flex flex-col sm:flex-row gap-3"
              >
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 px-5 py-3 rounded-full bg-cream/10 border border-cream/20 text-cream placeholder:text-cream/50 font-body focus:outline-none focus:border-terracotta transition-colors"
                  disabled={status === 'loading'}
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="px-6 py-3 bg-terracotta text-white rounded-full font-body font-medium hover:bg-terracotta-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {status === 'loading' ? (
                    'Subscribing...'
                  ) : (
                    <>
                      Subscribe
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </motion.form>
            )}
          </AnimatePresence>

          {status === 'error' && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-red-300 font-body text-sm mt-3"
            >
              {message}
            </motion.p>
          )}

          <p className="text-cream/50 font-body text-xs mt-4">
            Unsubscribe anytime. We respect your inbox.
          </p>
        </div>
      </motion.div>
    );
  }

  // Prompt variant - floating soft prompt
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      className="fixed bottom-6 left-6 right-6 md:left-auto md:right-6 md:w-96 bg-white rounded-2xl shadow-2xl border border-stone-200 overflow-hidden z-40"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-3 right-3 p-1 text-stone-400 hover:text-stone-600 transition-colors"
        aria-label="Close"
      >
        <X size={20} />
      </button>

      <div className="p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-terracotta/10 rounded-full flex items-center justify-center">
            <Mail className="text-terracotta" size={20} />
          </div>
          <h4 className="font-heading text-lg text-forest">
            Enjoying these resources?
          </h4>
        </div>

        <p className="text-stone-600 font-body text-sm mb-4">
          Get weekly wellness tips and new content delivered straight to your inbox.
        </p>

        <AnimatePresence mode="wait">
          {status === 'success' ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center gap-2 text-emerald-600 bg-emerald-50 rounded-lg p-3"
            >
              <CheckCircle size={20} />
              <span className="font-body text-sm">{message}</span>
            </motion.div>
          ) : (
            <motion.form
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onSubmit={handleSubmit}
              className="space-y-3"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="w-full px-4 py-2.5 rounded-lg bg-stone-50 border border-stone-200 text-forest placeholder:text-stone-400 font-body text-sm focus:outline-none focus:border-terracotta transition-colors"
                disabled={status === 'loading'}
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full px-4 py-2.5 bg-forest text-cream rounded-lg font-body text-sm font-medium hover:bg-forest-dark transition-colors disabled:opacity-50"
              >
                {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
              </button>
            </motion.form>
          )}
        </AnimatePresence>

        {status === 'error' && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-red-500 font-body text-xs mt-2"
          >
            {message}
          </motion.p>
        )}

        <button
          onClick={onClose}
          className="w-full text-center text-stone-400 font-body text-xs mt-3 hover:text-stone-600 transition-colors"
        >
          Maybe later
        </button>
      </div>
    </motion.div>
  );
};

export default NewsletterSignup;
